import { CalendarFilled } from '@ant-design/icons'
import { DatePicker, Input, message } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { requestSalaryContract } from '../../../../api/aopsHR.api'
import AopsCommonModalLayout from '../../../../components/apos/modal/AopsCommonModalLayout'
import '../orgChart.scss'
import { workTypeMap } from '../utils/employmentMappings'
const { TextArea } = Input

// 연봉 계약서 모달
const SalaryContractModal = ({
  open,
  onClose,
  personalInfo,
  employmentInfo,
  onRequest,
}) => {
  // 템플릿 기본값
  const TEMPLATE_DEFAULTS = {
    workplace: '서울시 강남구 테헤란로 501, 5층 하우투약 본사',
    workTime: '09시 30분 ~ 18시 30분',
    workType: '정규직/계약직',
    paymentMethod: '월 분할 지급',
    paymentCycle: '매월',
    paymentDate: '매월 5일 (휴일의 경우 전일 지급)',
    severancePayStandard: '근로기준법에 따름',
    adjustmentTime: '매년 1회',
    renewalConditions: '성과 평가 및 회사 정책에 따름',
  }

  const [formData, setFormData] = useState({
    contractStartDate: null,
    contractEndDate: null,
    department: '',
    position: '',
    rank: '',
    workType: '',
    workTime: '',
    workplace: '',
    annualSalary: '', // 연봉 총액
    baseSalary: '', // 기본급
    bonus: '', // 고정상여
    incentive: '', // 인센티브
    specialTerms: '', // 특약 사항 (선택)
    salaryComponents: '',
    paymentMethod: '',
    paymentCycle: '',
    paymentDate: '',
    severancePayStandard: '',
    adjustmentTime: '',
    renewalConditions: '',
  })

  const [isProcessing, setIsProcessing] = useState(false)

  // 모달이 열릴 때 personalInfo와 employmentInfo를 기반으로 초기값 설정
  useEffect(() => {
    if (open && (personalInfo || employmentInfo)) {
      // workType 매핑
      const mappedWorkType =
        workTypeMap[employmentInfo?.workType] || employmentInfo?.workType || ''

      // joinDate를 계약 시작일로 사용
      const contractStartDate = employmentInfo?.joinDate
        ? dayjs(employmentInfo.joinDate)
        : null

      setFormData({
        contractStartDate: contractStartDate,
        contractEndDate: null, // 종료일은 사용자가 입력
        department:
          employmentInfo?.department || employmentInfo?.teamName || '',
        position: employmentInfo?.position || '',
        rank: employmentInfo?.rank || '',
        workType: mappedWorkType || TEMPLATE_DEFAULTS.workType,
        workTime: TEMPLATE_DEFAULTS.workTime,
        workplace: TEMPLATE_DEFAULTS.workplace,
        annualSalary: '',
        baseSalary: '',
        bonus: '',
        incentive: '',
        specialTerms: '',
        salaryComponents: '기본급',
        paymentMethod: TEMPLATE_DEFAULTS.paymentMethod,
        paymentCycle: TEMPLATE_DEFAULTS.paymentCycle,
        paymentDate: TEMPLATE_DEFAULTS.paymentDate,
        severancePayStandard: TEMPLATE_DEFAULTS.severancePayStandard,
        adjustmentTime: TEMPLATE_DEFAULTS.adjustmentTime,
        renewalConditions: TEMPLATE_DEFAULTS.renewalConditions,
      })
    }
  }, [open, personalInfo, employmentInfo])

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // 숫자 포맷팅 함수 (천 단위 구분자 추가, 최대 10억 제한)
  const formatNumber = (value) => {
    // 숫자가 아닌 문자 제거
    const numbersOnly = value.replace(/[^\d]/g, '')
    if (!numbersOnly) return ''
    // 숫자로 변환
    const numValue = parseInt(numbersOnly, 10)
    // 최대값 999,999,999 (10억 미만)로 제한
    const maxValue = 999999999
    const limitedValue = numValue > maxValue ? maxValue : numValue
    // 천 단위 구분자 추가
    return limitedValue.toLocaleString('ko-KR')
  }

  // 연봉 총액 변경 핸들러
  const handleAnnualSalaryChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('annualSalary', formattedValue)
  }

  // 기본급 변경 핸들러
  const handleBaseSalaryChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('baseSalary', formattedValue)
  }

  // 고정상여 변경 핸들러
  const handleBonusChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('bonus', formattedValue)
  }

  // 인센티브 변경 핸들러
  const handleIncentiveChange = (e) => {
    const formattedValue = formatNumber(e.target.value)
    handleInputChange('incentive', formattedValue)
  }

  // 필수 필드 검증 함수 (API에 전송되는 필드만 검증)
  const validateForm = () => {
    const errors = []

    // 계약 시작일 (API 전송 필드)
    if (!formData.contractStartDate) {
      errors.push('계약 시작일을 입력해주세요.')
    }

    // 계약 종료일 (API 전송 필드)
    if (!formData.contractEndDate) {
      errors.push('계약 종료일을 입력해주세요.')
    }

    // 직급 (API 전송 필드)
    if (!formData.rank || formData.rank.trim() === '') {
      errors.push('직급을 입력해주세요.')
    }

    // 연봉 총액 (API 전송 필드)
    if (!formData.annualSalary || formData.annualSalary.trim() === '') {
      errors.push('연봉 총액을 입력해주세요.')
    }

    // 기본급 (API 전송 필드)
    if (!formData.baseSalary || formData.baseSalary.trim() === '') {
      errors.push('기본급을 입력해주세요.')
    }

    // 고정상여 (API 전송 필드)
    if (!formData.bonus || formData.bonus.trim() === '') {
      errors.push('고정상여를 입력해주세요.')
    }

    // 인센티브 (API 전송 필드)
    if (!formData.incentive || formData.incentive.trim() === '') {
      errors.push('인센티브를 입력해주세요.')
    }

    return errors
  }

  const handleRequest = async () => {
    // 필수 필드 검증
    const validationErrors = validateForm()
    if (validationErrors.length > 0) {
      message.error(validationErrors[0])
      return
    }

    setIsProcessing(true)
    try {
      // 서버로 전송할 데이터 매핑 (CreateSalaryContractRequest DTO 구조에 맞춤)
      const requestData = {
        employeeId: personalInfo?.employeeId || null,
        contractType: 'SALARY', // 연봉 계약서는 SALARY
        contractStartDate: formData.contractStartDate
          ? dayjs(formData.contractStartDate).format('YYYY-MM-DD')
          : null,
        contractEndDate: formData.contractEndDate
          ? dayjs(formData.contractEndDate).format('YYYY-MM-DD')
          : null,
        annualSalaryAmount: formData.annualSalary
          ? parseFloat(formData.annualSalary.replace(/,/g, ''))
          : null,
        baseSalaryAmount: formData.baseSalary
          ? parseFloat(formData.baseSalary.replace(/,/g, ''))
          : null,
        bonusAmount: formData.bonus
          ? parseFloat(formData.bonus.replace(/,/g, ''))
          : null,
        incentiveAmount: formData.incentive
          ? parseFloat(formData.incentive.replace(/,/g, ''))
          : null,
        jobLevel: formData.rank || '',
        // specialTerms: formData.specialTerms || null,
      }

      const userId = personalInfo?.userId || null

      if (!userId) {
        message.error('사용자 정보를 찾을 수 없습니다.')
        setIsProcessing(false)
        return
      }

      const res = await requestSalaryContract(requestData, { userId })
      console.log('requestSalaryContract res::', res)
      if (res.status === 200) {
        message.success('서명 요청이 완료되었습니다.')
        onRequest?.(requestData)
        onClose()
      } else {
        message.error('서명 요청 중 오류가 발생했습니다.')
      }
    } catch (error) {
      console.error('서명 요청 오류:', error)
      message.error('서명 요청 중 오류가 발생했습니다.')
    } finally {
      setIsProcessing(false)
    }
  }

  const requesterName = personalInfo?.name || '-'

  return (
    <AopsCommonModalLayout
      open={open}
      onClose={onClose}
      title="연봉계약서 서명하기"
    >
      <div className="form-container">
        <div className="form-wrapper">
          {/* 요청 진행 중 정보 */}
          <div className="contract-form-header">
            <h3 className="contract-form-subtitle">연봉 계약서</h3>
            <div className="contract-form-requester">
              요청 진행 중 : {requesterName}
            </div>
          </div>

          <div className="contract-form-table-wrapper">
            {/* 좌측 컬럼 */}
            <div className="contract-form-column">
              {/* 계약 기간 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">계약 기간</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">계약 시작일</label>
                    <DatePicker
                      placeholder="시작일을 입력해주세요"
                      value={formData.contractStartDate}
                      onChange={(date) =>
                        handleInputChange('contractStartDate', date)
                      }
                      suffixIcon={<CalendarFilled />}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">계약 종료일</label>
                    <DatePicker
                      placeholder="종료일을 입력해주세요"
                      value={formData.contractEndDate}
                      onChange={(date) =>
                        handleInputChange('contractEndDate', date)
                      }
                      suffixIcon={<CalendarFilled />}
                      disabled={formData.workType === '정규직'}
                    />
                  </div>
                </div>
              </div>

              {/* 소속 정보 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">소속 정보</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">소속 부서</label>
                    <Input
                      placeholder="소속 부서를 입력해주세요"
                      value={formData.department}
                      onChange={(e) =>
                        handleInputChange('department', e.target.value)
                      }
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">직무</label>
                    <Input
                      placeholder="직무를 입력해주세요"
                      value={formData.position}
                      onChange={(e) =>
                        handleInputChange('position', e.target.value)
                      }
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">직급</label>
                    <Input
                      placeholder="직급을 입력해주세요"
                      value={formData.rank}
                      onChange={(e) =>
                        handleInputChange('rank', e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              {/* 근무 조건 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">근무 조건</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">근무 형태</label>
                    <Input
                      placeholder="근무 형태"
                      value={formData.workType}
                      onChange={(e) =>
                        handleInputChange('workType', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">근무 시간</label>
                    <Input
                      placeholder="근무 시간"
                      value={formData.workTime}
                      onChange={(e) =>
                        handleInputChange('workTime', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">근무 장소</label>
                    <TextArea
                      placeholder="근무 장소"
                      value={formData.workplace}
                      onChange={(e) =>
                        handleInputChange('workplace', e.target.value)
                      }
                      autoSize={{ minRows: 2, maxRows: 4 }}
                      style={{ resize: 'none' }}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 우측 컬럼 */}
            <div className="contract-form-column">
              {/* 연봉 조건 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">연봉 조건</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">연봉 총액</label>
                    <Input
                      placeholder="연봉 총액을 입력해주세요"
                      value={formData.annualSalary}
                      onChange={handleAnnualSalaryChange}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">기본급</label>
                    <Input
                      placeholder="기본급을 입력해주세요"
                      value={formData.baseSalary}
                      onChange={handleBaseSalaryChange}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">고정상여</label>
                    <Input
                      placeholder="고정상여를 입력해주세요"
                      value={formData.bonus}
                      onChange={handleBonusChange}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">인센티브</label>
                    <Input
                      placeholder="인센티브를 입력해주세요"
                      value={formData.incentive}
                      onChange={handleIncentiveChange}
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">지급 방식</label>
                    <Input
                      placeholder="지급 방식"
                      value={formData.paymentMethod}
                      onChange={(e) =>
                        handleInputChange('paymentMethod', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">지급 주기</label>
                    <Input
                      placeholder="지급 주기"
                      value={formData.paymentCycle}
                      onChange={(e) =>
                        handleInputChange('paymentCycle', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">지급일</label>
                    <Input
                      placeholder="지급일"
                      value={formData.paymentDate}
                      onChange={(e) =>
                        handleInputChange('paymentDate', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* 퇴직금 */}
              <div className="contract-form-row">
                <div className="contract-form-category-label">퇴직금</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">산정 기준</label>
                  <Input
                    placeholder="산정 기준"
                    value={formData.severancePayStandard}
                    onChange={(e) =>
                      handleInputChange('severancePayStandard', e.target.value)
                    }
                    readOnly
                  />
                </div>
              </div>

              {/* 연봉 조정 */}
              <div className="contract-form-row contract-form-row-multi">
                <div className="contract-form-category-label">연봉 조정</div>
                <div className="contract-form-field-cells">
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">조정 시기</label>
                    <Input
                      placeholder="조정 시기"
                      value={formData.adjustmentTime}
                      onChange={(e) =>
                        handleInputChange('adjustmentTime', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                  <div className="contract-form-field-cell">
                    <label className="contract-form-label">재계약 조건</label>
                    <Input
                      placeholder="재계약 조건"
                      value={formData.renewalConditions}
                      onChange={(e) =>
                        handleInputChange('renewalConditions', e.target.value)
                      }
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* 특약 사항 */}
              {/* <div className="contract-form-row">
                <div className="contract-form-category-label">특약 사항</div>
                <div className="contract-form-field-cell">
                  <label className="contract-form-label">특약 사항</label>
                  <Input
                    placeholder="특약 사항을 입력해주세요 (선택)"
                    value={formData.specialTerms}
                    onChange={(e) =>
                      handleInputChange('specialTerms', e.target.value)
                    }
                  />
                </div>
              </div> */}
            </div>
          </div>

          {/* 하단 버튼 */}
          <div className="popup-footer">
            <button className="btn-cancel" onClick={onClose}>
              취소
            </button>
            <button
              className="btn-submit"
              onClick={handleRequest}
              disabled={isProcessing}
            >
              {isProcessing ? '요청 중...' : '서명 요청하기'}
            </button>
          </div>
        </div>
      </div>
    </AopsCommonModalLayout>
  )
}

export default SalaryContractModal
